import React, { useState } from 'react';
import { format, parseISO, addMonths, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend } from 'date-fns';
import { useShift } from '../context/ShiftContext';
import { useUser } from '../context/UserContext';
import { CalendarIcon } from 'lucide-react';
const RequestShifts = () => {
  const {
    shifts,
    holidays,
    addRequest
  } = useShift();
  const {
    user
  } = useUser();
  const [startDate, setStartDate] = useState(new Date());
  const [selectedShifts, setSelectedShifts] = useState<number[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  // Get days for next three months
  const firstDay = startOfMonth(startDate);
  const lastDay = endOfMonth(addMonths(startDate, 2));
  const days = eachDayOfInterval({
    start: firstDay,
    end: lastDay
  });
  const handleShiftSelection = (shiftId: number) => {
    setSelectedShifts(prev => prev.includes(shiftId) ? prev.filter(id => id !== shiftId) : [...prev, shiftId]);
  };
  const handleSubmitRequest = () => {
    if (!user) return;
    selectedShifts.forEach(shiftId => {
      addRequest({
        userId: user.id,
        shiftId
      });
    });
    setSuccessMessage('Your shift requests have been submitted successfully!');
    setSelectedShifts([]);
    setTimeout(() => {
      setSuccessMessage('');
    }, 5000);
  };
  return <div className="space-y-6" data-id="element-404">
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-405">
        <h2 className="text-2xl font-bold mb-4 text-gray-800" data-id="element-406">
          Request Shifts
        </h2>
        <p className="text-gray-600 mb-4" data-id="element-407">
          Select the shifts you would like to work. Your requests will be
          reviewed by the administrator.
        </p>
        {successMessage && <div className="border px-4 py-3 rounded mb-4" style={{
        backgroundColor: '#D1FAE5',
        borderColor: '#10B981',
        color: '#065F46'
      }} data-id="element-408">
            {successMessage}
          </div>}
        <div className="overflow-x-auto" data-id="element-409">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-410">
            <thead data-id="element-411">
              <tr data-id="element-412">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-413">
                  Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-414">
                  Day
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-415">
                  Holiday
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-416">
                  Night Shift
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-417">
                  Weekend Shift
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-418">
              {days.map(day => {
              const dateString = format(day, 'yyyy-MM-dd');
              const holiday = holidays.find(h => h.date === dateString);
              const nightShift = shifts.find(s => s.date === dateString && s.type === 'night');
              const weekendShift = shifts.find(s => s.date === dateString && s.type === 'weekend');
              const holidayShift = shifts.find(s => s.date === dateString && s.type === 'holiday');
              const isWeekendDay = isWeekend(day);
              // Check if shifts are at capacity
              const nightShiftFull = nightShift && nightShift.assignedTo.length >= 1;
              const weekendShiftFull = weekendShift && weekendShift.assignedTo.length >= 2;
              const holidayShiftFull = holidayShift && holidayShift.assignedTo.length >= 1;
              return <tr key={dateString} className={`${isWeekendDay ? 'bg-gray-50' : ''} ${holiday ? 'bg-red-50' : ''}`} style={holiday ? {
                backgroundColor: '#FEF2F2'
              } : {}} data-id="element-419">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-420">
                      {format(day, 'MM/dd/yyyy')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-id="element-421">
                      {format(day, 'EEEE')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-id="element-422">
                      {holiday?.name || ''}
                      {holiday && holidayShift && <div className="mt-1" data-id="element-423">
                          <label className="inline-flex items-center" data-id="element-424">
                            <input type="checkbox" className="form-checkbox h-4 w-4 text-blue-600" checked={selectedShifts.includes(holidayShift.id)} onChange={() => handleShiftSelection(holidayShift.id)} disabled={holidayShiftFull} data-id="element-425" />
                            <span className="ml-2 text-xs" data-id="element-426">Request</span>
                          </label>
                        </div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-id="element-427">
                      {nightShift && <label className="inline-flex items-center" data-id="element-428">
                          <input type="checkbox" className="form-checkbox h-4 w-4 text-blue-600" checked={selectedShifts.includes(nightShift.id)} onChange={() => handleShiftSelection(nightShift.id)} disabled={nightShiftFull} data-id="element-429" />
                          <span className="ml-2" data-id="element-430">Request Night</span>
                        </label>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-id="element-431">
                      {isWeekendDay && weekendShift && <label className="inline-flex items-center" data-id="element-432">
                          <input type="checkbox" className="form-checkbox h-4 w-4 text-blue-600" checked={selectedShifts.includes(weekendShift.id)} onChange={() => handleShiftSelection(weekendShift.id)} disabled={weekendShiftFull} data-id="element-433" />
                          <span className="ml-2" data-id="element-434">
                            Request Weekend{' '}
                            {weekendShift.assignedTo.length > 0 ? `(${2 - weekendShift.assignedTo.length} spot${2 - weekendShift.assignedTo.length !== 1 ? 's' : ''} left)` : ''}
                          </span>
                        </label>}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex justify-end" data-id="element-435">
          <button onClick={handleSubmitRequest} disabled={selectedShifts.length === 0} className={`flex items-center px-4 py-2 rounded-md text-white ${selectedShifts.length === 0 ? 'bg-gray-300 cursor-not-allowed text-gray-500' : ''}`} style={selectedShifts.length > 0 ? {
          backgroundColor: '#4772B0'
        } : {}} onMouseEnter={e => {
          if (selectedShifts.length > 0) {
            e.currentTarget.style.backgroundColor = '#EF5350';
          }
        }} onMouseLeave={e => {
          if (selectedShifts.length > 0) {
            e.currentTarget.style.backgroundColor = '#4772B0';
          }
        }} data-id="element-436">
            <CalendarIcon size={16} className="mr-2" data-id="element-437" />
            Submit Requests ({selectedShifts.length})
          </button>
        </div>
      </div>
    </div>;
};
export default RequestShifts;